import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types ───

export interface HomePatientsServedRow {
  count: number;
}

export interface HomeQueueCountRow {
  total: number;
  waiting: number;
  called: number;
  serving: number;
}

export interface HomeCollectionsRow {
  total_cents: number;
  payment_count: number;
}

export interface HomeOutstandingRow {
  total_cents: number;
  invoice_count: number;
}

export interface HomeAlertRow {
  total: number;
  critical: number;
  warning: number;
}

export interface HomeQueueEntryRow {
  id: string;
  queue_number: number | null;
  patient_id: string;
  patient_name: string | null;
  service_name: string | null;
  provider_name: string | null;
  status: string;
  notes: string | null;
}

export interface HomeScheduleRow {
  id: string;
  patient_id: string | null;
  patient_name: string | null;
  service_name: string | null;
  start_time: string | null;
  duration_minutes: number | null;
  date: string | null;
  status: string | null;
}

export interface HomeRecentPatientRow {
  id: string;
  full_name: string | null;
  last_visited_at: string | null;
  photo_url: string | null;
  queue_status: string | null;
  encounter_status: string | null;
}

// ─── Queries ───

/**
 * Patients served today — count of distinct patients with encounters
 * created within [dayStartUtc, dayEndUtc), excluding cancelled/voided.
 */
export function qHomePatientsServedToday(
  dayStartUtc: string,
  dayEndUtc: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'patients-served', dayStartUtc] as const,
    sql: `SELECT COUNT(DISTINCT patient_id) AS count
      FROM patient_encounters
      WHERE (started_at >= ? AND started_at < ?)
        AND status NOT IN ('cancelled', 'voided')`,
    params: [dayStartUtc, dayEndUtc],
    watch: ['patient_encounters'],
  };
}

/**
 * Queue counts for today — total active, waiting, called, serving.
 */
export function qHomeQueueCounts(todayIso: string): QuerySpec<[string, string, string, string]> {
  return {
    key: ['home', 'queue-counts', todayIso] as const,
    sql: `SELECT
      COUNT(*) AS total,
      SUM(CASE WHEN status = 'waiting' THEN 1 ELSE 0 END) AS waiting,
      SUM(CASE WHEN status = 'called' THEN 1 ELSE 0 END) AS called,
      SUM(CASE WHEN status = 'serving' THEN 1 ELSE 0 END) AS serving
    FROM queue_entries
    WHERE queue_date = ? AND status IN ('waiting', 'called', 'serving', 'expected')`,
    params: [todayIso, todayIso, todayIso, todayIso],
    watch: ['queue_entries'],
  };
}

/**
 * Collections today — the actual money received today. Sums every payment that
 * was RECORDED today (by created_at), regardless of invoice status or the
 * payment's visit/invoice date, so partial payments, patient-level payments,
 * and payments entered today for an older visit are all counted as today's
 * cash. `total_cents` is in major units (pesos); amount_cents is cents → /100.
 */
export function qHomeCollectionsToday(
  dayStartUtc: string,
  dayEndUtc: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'collections', dayStartUtc] as const,
    sql: `SELECT
      COALESCE(SUM(ip.amount_cents), 0) / 100.0 AS total_cents,
      COUNT(*) AS payment_count
    FROM invoice_payments ip
    WHERE COALESCE(ip.created_at, ip.paid_at) >= ?
      AND COALESCE(ip.created_at, ip.paid_at) < ?`,
    params: [dayStartUtc, dayEndUtc],
    watch: ['invoice_payments'],
  };
}

/**
 * Outstanding invoices — unpaid total + count.
 */
export function qHomeOutstanding(): QuerySpec<[]> {
  return {
    key: ['home', 'outstanding'] as const,
    // Outstanding = finalized (issued) invoices with a remaining balance.
    // Balance (pesos) = grand_total − paid; amount_cents is cents, so /100.0.
    sql: `SELECT COALESCE(SUM(bal.balance), 0) AS total_cents, COUNT(*) AS invoice_count
    FROM (
      SELECT i.grand_total - COALESCE((SELECT SUM(ip.amount_cents) FROM invoice_payments ip
                                       WHERE ip.invoice_id = i.id), 0) / 100.0 AS balance
      FROM invoices i
      WHERE i.status <> 'draft'
    ) bal
    WHERE bal.balance > 0`,
    params: [],
    watch: ['invoices', 'invoice_payments'],
  };
}

export interface HomeNewOutstandingTodayRow {
  /** Unpaid remainder of today-created charges, in major units (pesos). */
  total_cents: number;
  /** Number of today-created invoices still carrying a balance. */
  invoice_count: number;
}

/**
 * PRD.250 — New Outstanding Today: the unpaid portion of charges CREATED today.
 * Distinct from Collections Today (cash received) and Total Outstanding (all
 * history). Scope = non-draft invoices created within today; value = the sum of
 * each such invoice's remaining balance (charge − payments applied to it),
 * clamped per-invoice to ≥ 0 so an overpaid invoice can't mask another's
 * balance. Payments-only (mirrors qHomeOutstanding), so it stays consistent
 * with the Total Outstanding card. `total_cents` is in major units (pesos).
 */
export function qHomeNewOutstandingToday(
  dayStartUtc: string,
  dayEndUtc: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'new-outstanding-today', dayStartUtc] as const,
    sql: `SELECT
      COALESCE(SUM(CASE WHEN bal.balance > 0 THEN bal.balance ELSE 0 END), 0) AS total_cents,
      COUNT(CASE WHEN bal.balance > 0 THEN 1 END) AS invoice_count
    FROM (
      SELECT i.grand_total - COALESCE((SELECT SUM(ip.amount_cents) FROM invoice_payments ip
                                       WHERE ip.invoice_id = i.id), 0) / 100.0 AS balance
      FROM invoices i
      WHERE i.status <> 'draft'
        AND i.created_at >= ? AND i.created_at < ?
    ) bal`,
    params: [dayStartUtc, dayEndUtc],
    watch: ['invoices', 'invoice_payments'],
  };
}

export interface HomeNewOutstandingTodayDetailRow {
  invoice_id: string;
  patient_id: string;
  patient_name: string | null;
  invoice_no: string | null;
  created_at: string | null;
  encounter_id: string | null;
  /** Remaining balance on this today-created invoice, in major units (pesos). */
  grand_total: number;
}

/**
 * PRD.250 — detail rows behind New Outstanding Today: each today-created,
 * non-draft invoice that still carries a balance, newest first. Powers the
 * New Outstanding Today drawer.
 */
export function qHomeNewOutstandingTodayDetail(
  dayStartUtc: string,
  dayEndUtc: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'new-outstanding-today-detail', dayStartUtc] as const,
    sql: `SELECT i.id AS invoice_id, i.patient_id, p.full_name AS patient_name,
      i.invoice_no, i.created_at, i.encounter_id,
      i.grand_total - COALESCE((SELECT SUM(ip.amount_cents) FROM invoice_payments ip
                                WHERE ip.invoice_id = i.id), 0) / 100.0 AS grand_total
    FROM invoices i
    LEFT JOIN patients p ON p.id = i.patient_id
    WHERE i.status <> 'draft'
      AND i.created_at >= ? AND i.created_at < ?
      AND (i.grand_total - COALESCE((SELECT SUM(ip.amount_cents) FROM invoice_payments ip
                                     WHERE ip.invoice_id = i.id), 0) / 100.0) > 0
    ORDER BY i.created_at DESC`,
    params: [dayStartUtc, dayEndUtc],
    watch: ['invoices', 'patients', 'invoice_payments'],
  };
}

/**
 * Patient alerts — count by alert_level.
 */
export function qHomeAlerts(): QuerySpec<[]> {
  return {
    key: ['home', 'alerts'] as const,
    sql: `SELECT
      COUNT(*) AS total,
      SUM(CASE WHEN alert_level = 'critical' THEN 1 ELSE 0 END) AS critical,
      SUM(CASE WHEN alert_level = 'warning' THEN 1 ELSE 0 END) AS warning
    FROM patient_notes
    WHERE alert_level IN ('critical', 'warning', 'info')`,
    params: [],
    watch: ['patient_notes'],
  };
}

/**
 * Queue snapshot — entries for today grouped by status.
 */
export function qHomeQueueSnapshot(todayIso: string): QuerySpec<[string]> {
  return {
    key: ['home', 'queue-snapshot', todayIso] as const,
    sql: `SELECT q.id, q.queue_number, q.patient_id, p.full_name AS patient_name,
      s.name AS service_name, q.status, q.notes
    FROM queue_entries q
    LEFT JOIN patients p ON p.id = q.patient_id
    LEFT JOIN appointment_services s ON s.id = q.service_id
    WHERE q.queue_date = ? AND q.status IN ('waiting', 'called', 'serving')
    ORDER BY
      CASE q.status WHEN 'called' THEN 0 WHEN 'serving' THEN 1 WHEN 'waiting' THEN 2 ELSE 3 END,
      q.position ASC`,
    params: [todayIso],
    watch: ['queue_entries', 'patients', 'appointment_services'],
  };
}

/**
 * Today's schedule — appointments for today.
 */
export function qHomeScheduleToday(todayIso: string): QuerySpec<[string]> {
  return {
    key: ['home', 'schedule', todayIso] as const,
    sql: `SELECT a.id, a.patient_id, p.full_name AS patient_name,
      s.name AS service_name, a.start_time, a.duration_minutes, a.date, a.notes AS status
    FROM appointments a
    LEFT JOIN patients p ON p.id = a.patient_id
    LEFT JOIN appointment_services s ON s.id = a.service_id
    WHERE a.date = ?
    ORDER BY a.start_time ASC`,
    params: [todayIso],
    watch: ['appointments', 'patients', 'appointment_services'],
  };
}

export function qHomeScheduleRange(
  startDate: string,
  endDate: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'schedule-range', startDate, endDate] as const,
    sql: `SELECT a.id, a.patient_id, p.full_name AS patient_name,
      s.name AS service_name, a.start_time, a.duration_minutes, a.date, a.notes AS status
    FROM appointments a
    LEFT JOIN patients p ON p.id = a.patient_id
    LEFT JOIN appointment_services s ON s.id = a.service_id
    WHERE a.date >= ? AND a.date <= ?
    ORDER BY a.date ASC, a.start_time ASC`,
    params: [startDate, endDate],
    watch: ['appointments', 'patients', 'appointment_services'],
  };
}

/**
 * Recent patients — by last_visited_at or recent encounters.
 */
export function qHomeRecentPatients(userId: string): QuerySpec<[string]> {
  return {
    key: ['home', 'recent-patients', userId] as const,
    // PRD.282 — per-user recently-viewed patients. Reads from
    // patient_recent_views (bumped when this user opens a patient), so each
    // staff member sees the patients THEY opened, most-recent first. `viewed_at`
    // is aliased to last_visited_at so the widget's row shape is unchanged.
    sql: `SELECT p.id, p.full_name, rv.viewed_at AS last_visited_at, p.photo_url,
      (SELECT q.status FROM queue_entries q WHERE q.patient_id = p.id AND q.status IN ('waiting', 'called', 'serving') ORDER BY q.created_at DESC LIMIT 1) AS queue_status,
      (SELECT e.status FROM patient_encounters e WHERE e.patient_id = p.id ORDER BY e.created_at DESC LIMIT 1) AS encounter_status
    FROM patient_recent_views rv
    JOIN patients p ON p.id = rv.patient_id
    WHERE rv.user_id = ?
    ORDER BY rv.viewed_at DESC
    LIMIT 5`,
    params: [userId],
    watch: ['patient_recent_views', 'patients', 'queue_entries', 'patient_encounters'],
  };
}

/**
 * Billing snapshot extras — oldest unpaid + top unpaid patient.
 */
export interface HomeBillingDetailRow {
  oldest_invoice_no: string | null;
  oldest_days: number | null;
  top_patient_name: string | null;
}

export function qHomeBillingDetails(): QuerySpec<[]> {
  return {
    key: ['home', 'billing-details'] as const,
    // Oldest unpaid + top unpaid patient — issued invoices only; "top" ranks by
    // remaining balance (grand_total − paid), not gross.
    sql: `SELECT
      (SELECT invoice_no FROM invoices WHERE status = 'issued' ORDER BY created_at ASC LIMIT 1) AS oldest_invoice_no,
      (SELECT CAST(julianday('now') - julianday(created_at) AS INTEGER) FROM invoices WHERE status = 'issued' ORDER BY created_at ASC LIMIT 1) AS oldest_days,
      (SELECT p.full_name FROM invoices i JOIN patients p ON p.id = i.patient_id WHERE i.status = 'issued' GROUP BY i.patient_id ORDER BY SUM(i.grand_total - COALESCE((SELECT SUM(ip.amount_cents) FROM invoice_payments ip WHERE ip.invoice_id = i.id), 0) / 100.0) DESC LIMIT 1) AS top_patient_name`,
    params: [],
    watch: ['invoices', 'patients', 'invoice_payments'],
  };
}

// ─── Drawer Detail Row Types ───

export interface HomePatientsServedDetailRow {
  encounter_id: string;
  patient_id: string;
  patient_name: string | null;
  started_at: string | null;
  status: string | null;
  provider_name: string | null;
  appointment_id: string | null;
}

export interface HomeQueueDetailRow {
  id: string;
  queue_number: number | null;
  patient_id: string;
  patient_name: string | null;
  service_name: string | null;
  provider_name: string | null;
  status: string;
  notes: string | null;
  arrived_at: string | null;
}

export interface HomeCollectionsDetailRow {
  payment_id: string;
  invoice_id: string | null;
  patient_id: string | null;
  patient_name: string | null;
  invoice_no: string | null;
  /** The payment amount in major units (pesos) — kept as `grand_total` for the drawer. */
  grand_total: number;
  updated_at: string | null;
  payment_method: string | null;
  encounter_id: string | null;
}

export interface HomeOutstandingDetailRow {
  invoice_id: string;
  patient_id: string;
  patient_name: string | null;
  invoice_no: string | null;
  grand_total: number;
  created_at: string | null;
  days_overdue: number;
  encounter_id: string | null;
}

export interface HomeAlertsDetailRow {
  note_id: string;
  patient_id: string;
  patient_name: string | null;
  alert_level: string;
  title: string | null;
  body: string | null;
  created_at: string | null;
}

// ─── Drawer Detail Queries ───

/**
 * Patients served today — detail rows with encounter info.
 * appointment_id is non-null when the encounter came from an appointment (vs walk-in).
 */
export function qHomePatientsServedDetail(
  dayStartUtc: string,
  dayEndUtc: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'patients-served-detail', dayStartUtc] as const,
    sql: `SELECT e.id AS encounter_id, e.patient_id, p.full_name AS patient_name,
      e.started_at, e.status, e.appointment_id,
      COALESCE(ap.display_name, ap.full_name) AS provider_name
    FROM patient_encounters e
    LEFT JOIN patients p ON p.id = e.patient_id
    LEFT JOIN appointment_providers ap ON ap.id = e.provider_id
    WHERE (e.started_at >= ? AND e.started_at < ?)
      AND e.status NOT IN ('cancelled', 'voided')
    ORDER BY e.started_at DESC`,
    params: [dayStartUtc, dayEndUtc],
    watch: ['patient_encounters', 'patients', 'appointment_providers'],
  };
}

/**
 * Queue detail — entries for today with provider name.
 */
export function qHomeQueueDetail(todayIso: string): QuerySpec<[string]> {
  return {
    key: ['home', 'queue-detail', todayIso] as const,
    sql: `SELECT q.id, q.queue_number, q.patient_id, p.full_name AS patient_name,
      s.name AS service_name,
      COALESCE(ap.display_name, ap.full_name) AS provider_name,
      q.status, q.notes, q.arrived_at
    FROM queue_entries q
    LEFT JOIN patients p ON p.id = q.patient_id
    LEFT JOIN appointment_services s ON s.id = q.service_id
    LEFT JOIN appointment_providers ap ON ap.id = q.provider_id
    WHERE q.queue_date = ? AND q.status IN ('waiting', 'called', 'serving')
    ORDER BY
      CASE q.status WHEN 'serving' THEN 0 WHEN 'called' THEN 1 WHEN 'waiting' THEN 2 ELSE 3 END,
      q.position ASC`,
    params: [todayIso],
    watch: ['queue_entries', 'patients', 'appointment_services', 'appointment_providers'],
  };
}

/**
 * Collections today detail — paid invoices with payment method.
 */
export function qHomeCollectionsDetail(
  dayStartUtc: string,
  dayEndUtc: string,
): QuerySpec<[string, string]> {
  return {
    key: ['home', 'collections-detail', dayStartUtc] as const,
    // One row per PAYMENT recorded today (partials + patient-level included),
    // regardless of invoice status/date, newest first. `grand_total` carries
    // the payment amount in major units so the drawer keeps reading the field.
    sql: `SELECT
      ip.id AS payment_id,
      ip.invoice_id,
      COALESCE(i.patient_id, ip.patient_id) AS patient_id,
      p.full_name AS patient_name,
      i.invoice_no,
      ip.amount_cents / 100.0 AS grand_total,
      i.encounter_id,
      COALESCE(ip.created_at, ip.paid_at) AS updated_at,
      ip.method AS payment_method
    FROM invoice_payments ip
    LEFT JOIN invoices i ON i.id = ip.invoice_id
    LEFT JOIN patients p ON p.id = COALESCE(i.patient_id, ip.patient_id)
    WHERE COALESCE(ip.created_at, ip.paid_at) >= ?
      AND COALESCE(ip.created_at, ip.paid_at) < ?
    ORDER BY COALESCE(ip.created_at, ip.paid_at) DESC`,
    params: [dayStartUtc, dayEndUtc],
    watch: ['invoices', 'patients', 'invoice_payments'],
  };
}

export interface HomeOutstandingByPatientRow {
  patient_id: string;
  patient_name: string | null;
  /** Remaining balance across the patient's unpaid invoices, in major units (pesos). */
  outstanding: number;
  unpaid_count: number;
  oldest_unpaid_at: string | null;
  last_payment_at: string | null;
  oldest_days: number;
  /** Earliest / latest invoice date (date_iso) among the patient's unpaid invoices. */
  first_invoice_date: string | null;
  last_invoice_date: string | null;
}

/**
 * PRD.249 — Outstanding Balances page: one row per patient who currently has a
 * balance due, aggregated from issued invoices with a remaining balance. Powers
 * both the summary cards and the patient table (same source of truth, §8.2).
 * `outstanding` is in major units (pesos), matching qHomeOutstanding.
 *
 * PRD.251 — optional invoice-date filtering by `invoices.date_iso` (the
 * canonical YYYY-MM-DD invoice date). When `dateFrom`/`dateTo` are given the
 * patient totals reflect only invoices within that date scope, so the page's
 * cards and table both follow the active filter. Outstanding logic itself is
 * unchanged (§8.2) — filtering only changes which invoices are included.
 */
export function qHomeOutstandingByPatient(opts?: {
  dateFrom?: string;
  dateTo?: string;
}): QuerySpec<string[]> {
  const where = [`i.status <> 'draft'`, `(i.grand_total - COALESCE(paid.cents, 0) / 100.0) > 0`];
  const params: string[] = [];
  if (opts?.dateFrom) {
    where.push('i.date_iso >= ?');
    params.push(opts.dateFrom);
  }
  if (opts?.dateTo) {
    where.push('i.date_iso <= ?');
    params.push(opts.dateTo);
  }
  return {
    key: ['home', 'outstanding-by-patient', opts?.dateFrom ?? '', opts?.dateTo ?? ''] as const,
    sql: `SELECT
      i.patient_id,
      p.full_name AS patient_name,
      SUM(i.grand_total - COALESCE(paid.cents, 0) / 100.0) AS outstanding,
      COUNT(*) AS unpaid_count,
      MIN(i.created_at) AS oldest_unpaid_at,
      MAX(lp.last_paid_at) AS last_payment_at,
      CAST(julianday('now') - julianday(MIN(i.created_at)) AS INTEGER) AS oldest_days,
      MIN(i.date_iso) AS first_invoice_date,
      MAX(i.date_iso) AS last_invoice_date
    FROM invoices i
    LEFT JOIN patients p ON p.id = i.patient_id
    LEFT JOIN (SELECT invoice_id, SUM(amount_cents) AS cents FROM invoice_payments GROUP BY invoice_id) paid
      ON paid.invoice_id = i.id
    LEFT JOIN (SELECT invoice_id, MAX(paid_at) AS last_paid_at FROM invoice_payments GROUP BY invoice_id) lp
      ON lp.invoice_id = i.id
    -- Outstanding is defined by remaining balance, not status: a stale "paid"
    -- invoice that still owes (e.g. after a payment was edited down) must count.
    -- Only drafts (not yet a real bill) are excluded.
    WHERE ${where.join('\n      AND ')}
    GROUP BY i.patient_id, p.full_name
    ORDER BY outstanding DESC`,
    params,
    watch: ['invoices', 'patients', 'invoice_payments'],
  };
}

/**
 * Outstanding detail — unpaid invoices with overdue days.
 */
export function qHomeOutstandingDetail(): QuerySpec<[]> {
  return {
    key: ['home', 'outstanding-detail'] as const,
    // grand_total here is the remaining balance (grand_total − paid), aliased so
    // the drawer keeps reading `grand_total`. Only issued invoices with balance > 0.
    sql: `SELECT i.id AS invoice_id, i.patient_id, p.full_name AS patient_name,
      i.invoice_no, i.created_at, i.encounter_id,
      i.grand_total - COALESCE((SELECT SUM(ip.amount_cents) FROM invoice_payments ip
                                WHERE ip.invoice_id = i.id), 0) / 100.0 AS grand_total,
      CAST(julianday('now') - julianday(i.created_at) AS INTEGER) AS days_overdue
    FROM invoices i
    LEFT JOIN patients p ON p.id = i.patient_id
    WHERE i.status <> 'draft'
      AND (i.grand_total - COALESCE((SELECT SUM(ip.amount_cents) FROM invoice_payments ip
                                     WHERE ip.invoice_id = i.id), 0) / 100.0) > 0
    ORDER BY days_overdue DESC`,
    params: [],
    watch: ['invoices', 'patients', 'invoice_payments'],
  };
}

/**
 * Patient alerts detail — notes with alert level, sorted by severity.
 */
export function qHomeAlertsDetail(): QuerySpec<[]> {
  return {
    key: ['home', 'alerts-detail'] as const,
    sql: `SELECT n.id AS note_id, n.patient_id, p.full_name AS patient_name,
      n.alert_level, n.title, n.body, n.created_at
    FROM patient_notes n
    LEFT JOIN patients p ON p.id = n.patient_id
    WHERE n.alert_level IN ('critical', 'warning', 'info')
    ORDER BY
      CASE n.alert_level WHEN 'critical' THEN 0 WHEN 'warning' THEN 1 ELSE 2 END,
      n.created_at DESC`,
    params: [],
    watch: ['patient_notes', 'patients'],
  };
}
